/**
 * QRCodeDisplay Component
 * Displays a QR code with download and copy functionality.
 */

import React, { useState, useEffect } from 'react';
import { Download, Copy, Check, ExternalLink, RefreshCw, QrCode } from 'lucide-react';
import axiosInstance from '../../axiosConfig';
import './QRCodeDisplay.css';

function QRCodeDisplay({
  type = 'store', // 'store' or 'product'
  productId = null,
  productName = null,
  size = 220,
  showUrl = true,
  showActions = true,
  onLoad = null,
  className = '',
}) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchQRCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, productId, size]);

  const fetchQRCode = async () => {
    setLoading(true);
    setError(null);

    try {
      let endpoint = '/qrcode/store';
      if (type === 'product' && productId) {
        endpoint = `/qrcode/product/${productId}`;
      }

      const response = await axiosInstance.get(endpoint, { params: { size } });

      if (response.data.success) {
        setQrData(response.data);
        onLoad?.(response.data);
      } else {
        setError(response.data.error || 'Failed to load QR code');
      }
    } catch (err) {
      console.error('Error fetching QR code:', err);
      setError(err.response?.data?.error || 'Failed to load QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      let endpoint = '/qrcode/download/store';
      let filename = 'store-qr-code.png';

      if (type === 'product' && productId) {
        endpoint = `/qrcode/download/product/${productId}`;
        filename = `product-qr-${productId}.png`;
      }

      const response = await axiosInstance.get(endpoint, {
        params: { size: 500 },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading QR code:', err);
    }
  };

  const handleCopyUrl = async () => {
    if (!qrData?.url) return;
    try {
      await navigator.clipboard.writeText(qrData.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying URL:', err);
    }
  };

  const handleOpenUrl = () => {
    if (qrData?.url) window.open(qrData.url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className={`qr-display ${className}`}>
        <div className="qr-frame qr-frame--placeholder" style={{ width: size + 32, height: size + 32 }}>
          <RefreshCw className="qr-spin" size={28} aria-hidden="true" />
          <span className="qr-placeholder-text">Generating QR…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`qr-display ${className}`}>
        <div className="qr-frame qr-frame--error" style={{ width: size + 32, height: size + 32 }}>
          <QrCode size={28} aria-hidden="true" />
          <span className="qr-placeholder-text">{error}</span>
          <button type="button" onClick={fetchQRCode} className="qr-retry-btn">
            <RefreshCw size={14} aria-hidden="true" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`qr-display ${className}`}>
      <div className="qr-frame">
        <img
          src={qrData?.qrCode}
          alt={type === 'store' ? 'Store QR Code' : `${productName || 'Product'} QR Code`}
          className="qr-image"
          style={{ width: size, height: size }}
        />
      </div>

      {productName && (
        <div className="qr-product-name" title={productName}>{productName}</div>
      )}

      {showUrl && qrData?.url && (
        <button
          type="button"
          onClick={handleCopyUrl}
          className="qr-url-chip"
          title={copied ? 'Copied!' : 'Click to copy URL'}
          aria-label={copied ? 'URL copied' : 'Copy URL'}
        >
          <span className="qr-url-text">{qrData.url}</span>
          <span className="qr-url-copy">
            {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          </span>
        </button>
      )}

      {showActions && (
        <div className="qr-actions">
          <button type="button" onClick={handleDownload} className="qr-btn qr-btn--primary">
            <Download size={16} aria-hidden="true" />
            <span>Download</span>
          </button>
          <button type="button" onClick={handleCopyUrl} className="qr-btn qr-btn--ghost">
            {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
            <span>{copied ? 'Copied' : 'Copy link'}</span>
          </button>
          <button type="button" onClick={handleOpenUrl} className="qr-btn qr-btn--ghost">
            <ExternalLink size={16} aria-hidden="true" />
            <span>Open</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default QRCodeDisplay;
